import { PlayerRef } from '@remotion/player';
import { useContext, useMemo } from 'react';
import {
  AbsoluteFill,
  getRemotionEnvironment,
  Img,
  staticFile,
} from 'remotion';
import { EditModeContext } from '../edit-mode';
import { SortedOutlines } from '../selection-border/sorted-outlines';
import { InspectedMemberOutline } from '../selection-border/inspected-member-outline';
import { isTimelineEmpty } from '../utils/is-timeline-empty';
import { useAssets, useBackgroundColor, useTracks } from '../utils/use-context';
import { useLocalUrls } from '../caching/load-to-blob-url';
import {
  getAssetSourceKind,
  useIsVariantUrlEnabled,
} from '../utils/find-asset-by-id';
import { EmptyCanvasPlaceholder } from './empty-canvas-placeholder';
import { BlurDrawingTool } from './blur-drawing-tool';
import { Layers } from './layers';
import { SolidDrawingTool } from './solid-drawing-tool';
import { TextInsertionTool } from './text-insertion-tool';
import { VideoMaskOverlay } from './video-mask-overlay';
import { ZoomRegionOverlay } from './zoom-region-overlay';
import { SmartGuideLines } from '../smart-guides';
import {
  isBlobBadgeEnabled,
  setBlobBadgeEnabled,
} from '@/shared/lib/editor-runtime-options';
import { PlayerOutputAudioMeterBridge } from '../timeline/use-player-output-audio-db';

export { setBlobBadgeEnabled };

/** 디버그: blob/remote 상태 뱃지 (Debug 패널에서 ON 시에만 표시) */
const BlobStatusBadge = () => {
  const localUrls = useLocalUrls();
  const { assets } = useAssets();
  const variantEnabled = useIsVariantUrlEnabled();

  const status = useMemo(() => {
    if (!isBlobBadgeEnabled()) return null;
    const videoAssets = Object.entries(assets).filter(
      ([, a]) => a.type === 'video',
    );
    if (videoAssets.length === 0) return null;

    let blob = 0;
    let original = 0;
    let staticCount = 0;
    const variantHeights: number[] = [];
    for (const [id, a] of videoAssets) {
      const src = getAssetSourceKind(a, localUrls[id]);
      if (src.kind === 'blob') blob += 1;
      else if (src.kind === 'variant') variantHeights.push(src.height);
      else if (src.kind === 'original') original += 1;
      else if (src.kind === 'static') staticCount += 1;
    }
    const total = videoAssets.length;
    const variant = variantHeights.length;

    // 단일 소스
    if (blob === total) return { label: 'BLOB', color: '#4CAF50' };
    if (variant === total) {
      const uniqHeights = Array.from(new Set(variantHeights)).sort(
        (a, b) => a - b,
      );
      const heightLabel = uniqHeights.map((h) => `${h}p`).join('/');
      return { label: `VARIANT ${heightLabel}`, color: '#FF9800' };
    }
    if (original === total) return { label: 'ORIGINAL', color: '#f44336' };
    if (staticCount === total) return { label: 'STATIC', color: '#9E9E9E' };

    // 혼합
    const parts: string[] = [];
    if (blob) parts.push(`B:${blob}`);
    if (variant) parts.push(`V:${variant}`);
    if (original) parts.push(`O:${original}`);
    if (staticCount) parts.push(`S:${staticCount}`);
    return { label: parts.join(' '), color: '#607D8B' };
  }, [assets, localUrls, variantEnabled]);

  if (!status) return null;

  return (
    <div
      style={{
        position: 'absolute',
        top: 6,
        right: 6,
        zIndex: 9999,
        padding: '12px 24px',
        borderRadius: 10,
        fontSize: 32,
        fontWeight: 'bold',
        color: '#fff',
        pointerEvents: 'none',
        backgroundColor: status.color,
      }}
    >
      {status.label}
    </div>
  );
};

export type MainCompositionProps = {
  playerRef: React.RefObject<PlayerRef | null> | null;
  isReadOnly?: boolean;
  enableMaskEditing?: boolean;
  shouldShowPlanWatermark?: boolean;
  showWatermarkInPlayer?: boolean;
  premountRemoteMedia?: boolean;
};

export const MainComposition = ({
  playerRef,
  isReadOnly = false,
  enableMaskEditing = false,
  shouldShowPlanWatermark = false,
  showWatermarkInPlayer = false,
  premountRemoteMedia = false,
}: MainCompositionProps) => {
  const { editMode } = useContext(EditModeContext);
  const { backgroundColor } = useBackgroundColor();
  const { tracks } = useTracks();

  const shouldRenderWatermark = useMemo(() => {
    if (!shouldShowPlanWatermark) return false;
    const env = getRemotionEnvironment();

    if (env.isRendering) {
      return true;
    }

    return showWatermarkInPlayer && env.isPlayer;
  }, [shouldShowPlanWatermark, showWatermarkInPlayer]);

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
      }}
    >
      {getRemotionEnvironment().isPlayer ? (
        <PlayerOutputAudioMeterBridge />
      ) : null}
      <Layers
        tracks={tracks}
        premountRemoteMedia={premountRemoteMedia}
      />

      {getRemotionEnvironment().isPlayer && !isReadOnly ? (
        <>
          <SortedOutlines />
          <InspectedMemberOutline />
          <SmartGuideLines />
          <ZoomRegionOverlay />
        </>
      ) : null}

      {getRemotionEnvironment().isPlayer &&
      !isReadOnly &&
      isTimelineEmpty(tracks) ? (
        <EmptyCanvasPlaceholder />
      ) : null}

      {editMode === 'draw-solid' && !isReadOnly && playerRef ? (
        <SolidDrawingTool playerRef={playerRef} />
      ) : null}

      {editMode === 'draw-blur' && !isReadOnly && playerRef ? (
        <BlurDrawingTool playerRef={playerRef} />
      ) : null}

      {editMode === 'create-text' && !isReadOnly && playerRef ? (
        <TextInsertionTool playerRef={playerRef} />
      ) : null}

      {enableMaskEditing && getRemotionEnvironment().isPlayer && !isReadOnly ? (
        <VideoMaskOverlay />
      ) : null}

      {getRemotionEnvironment().isPlayer ? <BlobStatusBadge /> : null}

      {shouldRenderWatermark ? (
        <AbsoluteFill
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            pointerEvents: 'none',
            zIndex: 999,
          }}
        >
          <Img
            src={staticFile('/res/img/watermark.png')}
            style={{
              maxWidth: '40%',
              maxHeight: '40%',
              opacity: 0.5,
            }}
          />
        </AbsoluteFill>
      ) : null}
    </AbsoluteFill>
  );
};
